<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatBytes, usagePercent } from '@/lib/storageFormat'
import type { AdminStorageQuota } from '@/types/organisations'

const props = defineProps<{
  quota: AdminStorageQuota | null
  loading: boolean
  // i18n key of the load error, or null.
  error: string | null
}>()

const emit = defineEmits<{
  edit: []
  clear: []
}>()

const { t } = useI18n()

const hasOverride = computed(() => props.quota?.quota_source === 'organisation_override')

const percent = computed(() =>
  props.quota ? usagePercent(props.quota.used_bytes, props.quota.effective_quota_bytes) : 0,
)

const barClass = computed(() => {
  if (props.quota?.is_over_quota || percent.value >= 100) return 'bg-red-500'
  if (percent.value >= 80) return 'bg-amber-500'
  return 'bg-emerald-500'
})
</script>

<template>
  <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6" data-testid="storage-card">
    <div class="flex items-start justify-between gap-4 flex-wrap">
      <div class="flex-1 min-w-0">
        <h2 class="text-lg font-semibold text-white">{{ t('admin.organisations.storage_title') }}</h2>
        <p class="mt-1 text-xs text-slate-500">{{ t('admin.organisations.storage_description') }}</p>
      </div>
      <div v-if="quota" class="flex items-center gap-3 shrink-0">
        <button
          v-if="hasOverride"
          type="button"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          data-testid="storage-clear"
          @click="emit('clear')"
        >
          {{ t('admin.organisations.storage_clear') }}
        </button>
        <button
          type="button"
          class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          data-testid="storage-edit"
          @click="emit('edit')"
        >
          {{ t('admin.organisations.storage_edit') }}
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="mt-4 space-y-2">
      <div class="h-4 bg-slate-700 rounded w-48 animate-pulse" />
      <div class="h-3 bg-slate-700 rounded w-full animate-pulse" />
    </div>

    <!-- Error state -->
    <p v-else-if="error" class="mt-4 text-sm text-red-300">{{ t(error) }}</p>

    <!-- Data state -->
    <div v-else-if="quota" class="mt-4 space-y-4">
      <div class="flex items-center gap-2 flex-wrap">
        <span class="text-sm text-white" data-testid="storage-used-of-quota">
          {{
            t('admin.organisations.storage_used_of_quota', {
              used: formatBytes(quota.used_bytes),
              quota: formatBytes(quota.effective_quota_bytes),
            })
          }}
        </span>
        <span
          class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
          :class="hasOverride ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'"
          data-testid="storage-source"
        >
          {{ hasOverride ? t('admin.organisations.storage_source_override') : t('admin.organisations.storage_source_default') }}
        </span>
        <span class="text-xs text-slate-500">{{ t('admin.organisations.storage_percent_used', { percent }) }}</span>
      </div>

      <div
        class="h-2 w-full rounded-full bg-slate-700 overflow-hidden"
        role="progressbar"
        :aria-valuenow="percent"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <!-- Dynamic width cannot be expressed as a static utility class. -->
        <div class="h-full rounded-full transition-all" :class="barClass" :style="{ width: `${percent}%` }" />
      </div>

      <p
        v-if="quota.is_over_quota"
        class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300"
        role="alert"
        data-testid="storage-over-quota"
      >
        {{ t('admin.organisations.storage_over_quota') }}
      </p>

      <div class="space-y-1 text-xs text-slate-500">
        <p v-if="quota.reserved_bytes > 0">
          {{ t('admin.organisations.storage_reserved', { reserved: formatBytes(quota.reserved_bytes) }) }}
        </p>
        <p data-testid="storage-derivatives">
          {{ t('admin.organisations.storage_derivatives', { derivatives: formatBytes(quota.derivative_bytes) }) }}
        </p>
      </div>

      <div>
        <h3 class="text-sm font-semibold text-white mb-2">{{ t('admin.organisations.storage_events_title') }}</h3>
        <p
          v-if="quota.events.length === 0"
          class="text-sm text-slate-400"
          data-testid="storage-events-empty"
        >
          {{ t('admin.organisations.storage_events_empty') }}
        </p>
        <table v-else class="w-full text-sm" data-testid="storage-events-table">
          <thead>
            <tr class="border-b border-slate-700">
              <th class="text-left py-2 pr-4 text-slate-400 font-medium">{{ t('admin.organisations.storage_event_name') }}</th>
              <th class="text-right py-2 px-4 text-slate-400 font-medium">{{ t('admin.organisations.storage_event_used') }}</th>
              <th class="text-right py-2 pl-4 text-slate-400 font-medium">{{ t('admin.organisations.storage_event_items') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="event in quota.events" :key="event.id" class="border-b border-slate-700 last:border-0">
              <td class="py-2 pr-4 text-white">
                {{ event.name }}
                <span class="ml-1 text-xs font-mono text-slate-500">{{ event.slug }}</span>
              </td>
              <td class="py-2 px-4 text-right text-slate-300">{{ formatBytes(event.used_bytes) }}</td>
              <td class="py-2 pl-4 text-right text-slate-300">{{ event.item_count }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
